// ./components/QueueScreen/TopBar.jsx
import React, { useRef, useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import "material-design-icons/iconfont/material-icons.css";
import { useAuth } from "../../contexts/AuthContext"; //  add this

export default function TopBar({
  stationId,
  displayName,
  onToggleDarkMode,
  isDark,
  onOpenSettings,
  onBumpAll,
  onExposeClick,
  showExposeButton = false,
  onSortClick,
  showSortButton = false,
  onPinClick,
  showPinButton = false,
  isPinActive = false,
  onToggleLockXAxis,
  isLockXAxisActive = false,
  onToggleLockYAxis,
  isLockYAxisActive = false,
  showAxisLockButtons = false,
}) {
  const { hasRole } = useAuth(); //  pulls from AuthProvider context

  // choose who can open settings
  const canOpenSettings = hasRole("settings_allow"); // adjust if you want expo/prep too

  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (e) {
      console.warn("Fullscreen toggle failed:", e);
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const showFreeformActions =
    showExposeButton || showSortButton || showPinButton || showAxisLockButtons;
  const freeformActionButtonClass =
    "w-24 bg-slate-600 dark:bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-700 dark:hover:bg-slate-400 active:scale-[0.98] touch-manipulation select-none";
  const freeformToggleButtonClass = (isActive) =>
    `w-24 px-4 py-2 rounded active:scale-[0.98] touch-manipulation select-none transition-colors ${
      isActive
        ? "bg-orange-500 text-white hover:bg-orange-600"
        : "bg-slate-600 dark:bg-slate-500 text-white hover:bg-slate-700 dark:hover:bg-slate-400"
    }`;

  return (
    <header className="relative grid h-12 grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 bg-white dark:bg-gray-800 border-b dark:border-gray-700 shadow-sm">
      <div className="flex items-center gap-2 text-xl text-blue-900 dark:text-blue-300 font-bold justify-self-start min-w-0">
        <span className="material-icons-outlined">bookmark</span>
        <span className="truncate">Station ID {stationId} - {displayName}</span>
      </div>

      <div className="flex items-center justify-center gap-2 justify-self-center">
        {showFreeformActions && (
          <>
            {showSortButton && (
              <button
                onClick={onSortClick}
                className={freeformActionButtonClass}
              >
                Sort
              </button>
            )}

            {showExposeButton && (
              <button
                onClick={onExposeClick}
                className={freeformActionButtonClass}
              >
                Expose
              </button>
            )}

            {showPinButton && (
              <button
                onClick={onPinClick}
                className={freeformToggleButtonClass(isPinActive)}
              >
                Pin
              </button>
            )}

            {showAxisLockButtons && (
              <button
                onClick={onToggleLockXAxis}
                className={freeformToggleButtonClass(isLockXAxisActive)}
              >
                Lock X
              </button>
            )}

            {showAxisLockButtons && (
              <button
                onClick={onToggleLockYAxis}
                className={freeformToggleButtonClass(isLockYAxisActive)}
              >
                Lock Y
              </button>
            )}
          </>
        )}
      </div>

      <div className="relative flex items-center justify-self-end gap-4" ref={menuRef}>
        <button
          onClick={onToggleDarkMode}
          className="relative w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center px-1 transition-colors duration-300"
        >
          <div
            className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isDark ? "translate-x-6" : "translate-x-0"
              }`}
          />
          <span className="absolute left-1 text-xs">
            <Icon icon={isDark ? "fluent-emoji:crescent-moon" : "fluent-emoji:sun"} className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>

        <button
          onClick={handleToggleFullscreen}
          className="text-blue-900 dark:text-blue-300 focus:outline-none mt-1.5"
          title="Toggle Fullscreen"
        >
          <span className="material-icons-outlined">
            {isFullscreen ? "close_fullscreen" : "open_in_full"}
          </span>
        </button>

        {/* Hamburger Menu Toggle */}
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className="text-blue-900 dark:text-blue-300 focus:outline-none mt-1.5"
        >
          <span className="material-icons-outlined">density_medium</span>
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-28 z-50 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {/*  Only show Settings if allowed */}
            {canOpenSettings && (
              <button
                onClick={() => {
                  onOpenSettings();
                  setMenuOpen(false);
                }}
                className="w-full text-left px-5 py-4 text-base font-semibold text-gray-800 transition hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <span className="inline-flex items-center gap-2">
                  <Icon icon="fluent-emoji:gear" className="h-5 w-5" aria-hidden="true" />
                  Settings
                </span>
              </button>
            )}

            <button
              onClick={() => {
                setShowConfirm(true);
                setMenuOpen(false);
              }}
              className="w-full text-left px-5 py-4 text-base font-semibold text-gray-800 transition hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Bump All Tickets
            </button>
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-80">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Confirm Action
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to bump all tickets?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onBumpAll();
                  setShowConfirm(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
